import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as api from '../api/reconciliation';
import type { CreateMatchBody, CreateRuleBody, RunReconciliationArgs, QueueGroup } from '../api/reconciliation';

const queueKey = (orgId: string, group?: string) =>
  ['admin', 'reconciliation', 'queue', orgId, group ?? 'all'] as const;
const matchKey = (orgId: string, matchId: string) =>
  ['admin', 'reconciliation', 'match', orgId, matchId] as const;
const candidatesKey = (orgId: string, transactionId: string) =>
  ['admin', 'reconciliation', 'candidates', orgId, transactionId] as const;
const rulesKey = (orgId: string) =>
  ['admin', 'reconciliation', 'rules', orgId] as const;
const unmatchedDocsKey = (orgId: string) =>
  ['admin', 'reconciliation', 'unmatched-docs', orgId] as const;

function invalidateQueue(qc: ReturnType<typeof useQueryClient>, orgId: string) {
  qc.invalidateQueries({ queryKey: ['admin', 'reconciliation', 'queue', orgId] });
}

// ── Queries ────────────────────────────────────────────────────────────────

export function useReviewQueue(orgId: string, group: QueueGroup = 'all') {
  return useQuery({
    queryKey: queueKey(orgId, group),
    queryFn: () => api.getReconciliationQueue(orgId, group),
      enabled: !!orgId,
      placeholderData: keepPreviousData
  });
}

export function useMatchDetail(orgId: string, matchId: string | null) {
  return useQuery({
    queryKey: matchKey(orgId, matchId ?? ''),
    queryFn: () => api.getMatchDetail(orgId, matchId!),
    enabled: !!orgId && !!matchId,
  });
}

/** Reserved for Phase 1 R3.1 admin UI (rules-list pane / unmatched-docs pane). Not yet consumed. */
export function useTransactionCandidates(
  orgId: string,
  transactionId: string | null,
) {
  return useQuery({
    queryKey: candidatesKey(orgId, transactionId ?? ''),
    queryFn: () => api.getTransactionCandidates(orgId, transactionId!),
    enabled: !!orgId && !!transactionId,
  });
}

/** Reserved for Phase 1 R3.1 admin UI (rules-list pane / unmatched-docs pane). Not yet consumed. */
export function useListRules(orgId: string) {
  return useQuery({
    queryKey: rulesKey(orgId),
    queryFn: () => api.listRules(orgId),
    enabled: !!orgId,
  });
}

/** Reserved for Phase 1 R3.1 admin UI (rules-list pane / unmatched-docs pane). Not yet consumed. */
export function useUnmatchedDocs(orgId: string) {
  return useQuery({
    queryKey: unmatchedDocsKey(orgId),
    queryFn: () => api.listUnmatchedDocs(orgId),
    enabled: !!orgId,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────

export function useRunReconciliation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: RunReconciliationArgs) => api.runReconciliation(orgId, args),
    onSuccess: () => {
      invalidateQueue(qc, orgId);
      toast.success('Reconciliation engine completed');
    },
    onError: (err: Error) => toast.error(`Engine run failed: ${err.message}`),
  });
}

export function useConfirmMatch(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, notes }: { matchId: string; notes?: string }) =>
      api.confirmMatch(orgId, matchId, notes),
    onSuccess: () => {
      invalidateQueue(qc, orgId);
      toast.success('Match confirmed');
    },
    onError: (err: Error) => toast.error(`Confirm failed: ${err.message}`),
  });
}

export function useRejectMatch(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, reason }: { matchId: string; reason: string }) =>
      api.rejectMatch(orgId, matchId, reason),
    onSuccess: () => {
      invalidateQueue(qc, orgId);
      toast.success('Match rejected');
    },
    onError: (err: Error) => toast.error(`Reject failed: ${err.message}`),
  });
}

export function useBulkConfirm(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (matchIds: string[]) => api.bulkConfirm(orgId, matchIds),
    onSuccess: (result) => {
      invalidateQueue(qc, orgId);
      const successes = result.results.filter((r) => r.success).length;
      const failures = result.results.length - successes;
      if (failures === 0) toast.success(`Confirmed ${successes} match${successes === 1 ? '' : 'es'}`);
      else toast.warning(`Confirmed ${successes}; ${failures} failed`);
    },
    onError: (err: Error) => toast.error(`Bulk confirm failed: ${err.message}`),
  });
}

export function useCreateManualMatch(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMatchBody) => api.createManualMatch(orgId, body),
    onSuccess: () => {
      invalidateQueue(qc, orgId);
      toast.success('Match created');
    },
    onError: (err: Error) => toast.error(`Create failed: ${err.message}`),
  });
}

export function useCreateRule(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRuleBody) => api.createRule(orgId, body),
    onSuccess: (_data, vars) => {
      if (!vars.dryRun) {
        qc.invalidateQueries({ queryKey: rulesKey(orgId) });
        invalidateQueue(qc, orgId);
        toast.success('Rule created');
      }
    },
    onError: (err: Error) => toast.error(`Rule creation failed: ${err.message}`),
  });
}

/** Reserved for Phase 1 R3.1 admin UI (rules-list pane / unmatched-docs pane). Not yet consumed. */
export function useDeleteRule(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => api.deleteRule(orgId, ruleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rulesKey(orgId) });
      toast.success('Rule deleted');
    },
    onError: (err: Error) => toast.error(`Delete failed: ${err.message}`),
  });
}
